// Ops Requests board — /requests (Sep 25, 2026)
//
// One list for everything the sales team needs from the Roof Team Manager.
// Robert works it top to bottom: urgent first, then needed-by, then oldest.
// Reps see their own requests; a TL sees the team's; ASM / Robert / leadership
// see everything. RLS is the scope — the toggles here only narrow it.
//
//   Tabs   : New · In progress · Waiting · Done (withdrawn shows greyed under Done)
//   Filters: category chips, homeowner/address search, Mine/Team/All scope
//   Stats  : leadership-only strip (open, avg days to close, oldest, per rep, per category)
//   ?focus=<id> (from a notification or text) shows that one request expanded.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTabParam } from "../lib/useTabParam";
import { useRealtimeTable } from "../lib/useRealtimeTable";
import OpsRequestCard from "../components/ops/OpsRequestCard";
import OpsRequestForm from "../components/ops/OpsRequestForm";
import {
  CATEGORIES, CATEGORY_ICON, CATEGORY_LABEL, OPEN_STATUSES,
  canFileRequests, canSeeAllRequests, computeStats, fetchOpenDispatchQuestionDeals, fetchRequests, fetchRoster,
  isLeadership, isOpen, sortForQueue,
  type ActorContext, type OpsCategory, type OpsRequest, type OpsStatus, type RosterLite,
} from "../lib/opsRequests";

type TabKey = "new" | "in_progress" | "waiting" | "done";
const TABS: Array<{ key: TabKey; label: string }> = [
  { key: "new",         label: "New" },
  { key: "in_progress", label: "In progress" },
  { key: "waiting",     label: "Waiting" },
  { key: "done",        label: "Done" },
];
type Scope = "mine" | "team" | "all";

export default function Requests() {
  const { member, role, isSuperUser } = useAuth();
  const [tab, setTab] = useTabParam<TabKey>("tab", "new", TABS.map((t) => t.key));
  const [params, setParams] = useSearchParams();
  const focusId = params.get("focus");

  const actor: ActorContext = useMemo(
    () => ({ memberId: member?.id ?? null, role, isSuperUser }),
    [member?.id, role, isSuperUser],
  );
  const canFile = canFileRequests(role, isSuperUser);
  const seeAll = canSeeAllRequests(role, isSuperUser);
  const lead = isLeadership(role, isSuperUser);
  const isTL = role === "team_lead" && !seeAll;

  const [rows, setRows] = useState<OpsRequest[]>([]);
  const [roster, setRoster] = useState<Record<string, RosterLite>>({});
  const [openQ, setOpenQ] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState<OpsCategory | "all">("all");
  const [scope, setScope] = useState<Scope>(seeAll ? "all" : isTL ? "team" : "mine");
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => { setScope(seeAll ? "all" : isTL ? "team" : "mine"); }, [seeAll, isTL]);

  const load = useCallback(async () => {
    try {
      setErr(null);
      const [reqs, people] = await Promise.all([fetchRequests(), fetchRoster()]);
      setRows(reqs);
      setRoster(Object.fromEntries(people.map((m) => [m.id, m])));
      setOpenQ(await fetchOpenDispatchQuestionDeals(reqs.filter(isOpen).map((r) => r.deal_id)));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't load requests.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  useRealtimeTable("ops_requests", () => { void load(); });

  // Scope (Mine / Team / All) — the stats strip totals from this so the
  // numbers always match the toggle the viewer is looking at.
  const scoped = useMemo(() => {
    if (!member?.id) return rows;
    if (scope === "all") return rows;
    if (scope === "team") {
      return rows.filter((r) => r.requested_by === member.id
        || roster[r.requested_by]?.team_lead_id === member.id);
    }
    // "mine": what I asked for, or (for the assignee) what's on my plate.
    return rows.filter((r) => r.requested_by === member.id || r.assigned_to === member.id);
  }, [rows, scope, member?.id, roster]);

  const focused = focusId ? rows.find((r) => r.id === focusId) ?? null : null;

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return scoped.filter((r) => {
      if (cat !== "all" && r.category !== cat) return false;
      if (!needle) return true;
      return [r.homeowner_name, r.property_address, r.body, roster[r.requested_by]?.full_name]
        .filter(Boolean).some((s) => (s as string).toLowerCase().includes(needle));
    });
  }, [scoped, q, cat, roster]);

  const byTab = useMemo(() => {
    const pick = (s: OpsStatus[]) => filtered.filter((r) => s.includes(r.status));
    return {
      new: sortForQueue(pick(["new"])),
      in_progress: sortForQueue(pick(["in_progress"])),
      waiting: sortForQueue(pick(["waiting"])),
      done: pick(["done", "withdrawn"]).sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1)),
    } as Record<TabKey, OpsRequest[]>;
  }, [filtered]);

  const counts = useMemo(() => {
    const c: Record<TabKey, number> = { new: 0, in_progress: 0, waiting: 0, done: 0 };
    for (const r of scoped) {
      if (r.status === "done" || r.status === "withdrawn") c.done++;
      else c[r.status as TabKey]++;
    }
    return c;
  }, [scoped]);

  const catCounts = useMemo(() => {
    const m = new Map<OpsCategory, number>();
    for (const r of scoped) if (OPEN_STATUSES.includes(r.status)) m.set(r.category, (m.get(r.category) ?? 0) + 1);
    return m;
  }, [scoped]);

  const stats = useMemo(() => (lead ? computeStats(scoped) : null), [lead, scoped]);
  const list = byTab[tab];
  const name = (id: string) => roster[id]?.full_name ?? "—";

  return (
    <div className="max-w-3xl mx-auto px-4 py-5 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-bardin-ink">🛠 Ops Requests</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {seeAll ? "Everything the sales team needs from Ops, in one list." : isTL ? "What you and your team have asked Ops for." : "What you've asked Ops for, and where it stands."}
          </p>
        </div>
        {canFile && member?.id && (
          <button type="button" onClick={() => setFormOpen(true)}
                  className="px-4 py-2 rounded-lg bg-bardin-orange text-white text-sm font-semibold shadow-sm">
            + New request
          </button>
        )}
      </div>

      {/* Leadership stats */}
      {stats && !focused && (
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat v={stats.open} l="Open now" sub={stats.urgent ? `${stats.urgent} urgent` : undefined} tone={stats.urgent ? "rose" : undefined} />
            <Stat v={stats.avgDaysToClose30 == null ? "—" : `${stats.avgDaysToClose30}d`} l="Avg to close · 30d" sub={`${stats.closed30} closed`} />
            <Stat v={stats.oldestDays == null ? "—" : `${stats.oldestDays}d`} l="Oldest open" sub={stats.overdue ? `${stats.overdue} overdue` : undefined} tone={stats.overdue ? "rose" : undefined} />
            <Stat v={stats.filed30} l="Filed · 30d" />
          </div>
          {(stats.perRep.length > 0 || stats.perCategory.length > 0) && (
            <details className="mt-2">
              <summary className="text-xs font-semibold text-slate-500 cursor-pointer">By rep · by category (last 30 days)</summary>
              <div className="mt-2 grid sm:grid-cols-2 gap-3 text-xs">
                <ul className="space-y-0.5">
                  {stats.perRep.map((p) => (
                    <li key={p.id} className="flex justify-between"><span>{name(p.id)}</span><span className="font-semibold tabular-nums">{p.count}</span></li>
                  ))}
                </ul>
                <ul className="space-y-0.5">
                  {stats.perCategory.map((c) => (
                    <li key={c.key} className="flex justify-between"><span>{CATEGORY_ICON[c.key]} {CATEGORY_LABEL[c.key]}</span><span className="font-semibold tabular-nums">{c.count}</span></li>
                  ))}
                </ul>
              </div>
              <p className="text-[11px] text-slate-400 mt-2">Withdrawn requests count as filed, never as closed.</p>
            </details>
          )}
        </div>
      )}

      {err && <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-800">{err}</div>}

      {/* Focus mode */}
      {focusId && (
        <div className="flex items-center justify-between rounded-lg bg-sky-50 border border-sky-200 px-3 py-2 text-sm text-sky-900">
          <span>Showing one request.</span>
          <button type="button" className="font-semibold underline"
                  onClick={() => { const next = new URLSearchParams(params); next.delete("focus"); setParams(next, { replace: true }); }}>
            Show all
          </button>
        </div>
      )}
      {focusId && !loading && !focused && (
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-3 text-sm text-slate-600">
          That request isn't in your view (it may have been filed by someone outside your team, or the link is old).
        </div>
      )}
      {focused && (
        <OpsRequestCard r={focused} actor={actor} roster={roster} onChanged={() => void load()}
                        defaultExpanded hasOpenQuestion={openQ.has(focused.deal_id)} />
      )}

      {!focusId && (
        <>
          {/* Scope + search */}
          <div className="flex flex-wrap gap-2 items-center">
            {(seeAll || isTL) && (
              <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden text-xs font-semibold">
                {(isTL ? (["mine", "team"] as Scope[]) : (["mine", "all"] as Scope[])).map((s) => (
                  <button key={s} type="button" onClick={() => setScope(s)}
                          className={`px-3 py-1.5 ${scope === s ? "bg-bardin-ink text-white" : "bg-white text-slate-600"}`}>
                    {s === "mine" ? "Mine" : s === "team" ? "Team" : "All"}
                  </button>
                ))}
              </div>
            )}
            <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search homeowner, address, text…"
                   className="flex-1 min-w-[10rem] rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
          </div>

          {/* Category chips */}
          <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
            <button type="button" onClick={() => setCat("all")}
                    className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold border ${cat === "all" ? "bg-bardin-ink text-white border-bardin-ink" : "bg-white text-slate-600 border-slate-300"}`}>
              All
            </button>
            {CATEGORIES.map((c) => (
              <button key={c.key} type="button" onClick={() => setCat(c.key)}
                      className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold border ${cat === c.key ? "bg-bardin-ink text-white border-bardin-ink" : "bg-white text-slate-600 border-slate-300"}`}>
                {c.icon} {c.label}{catCounts.get(c.key) ? <span className="ml-1 opacity-70 tabular-nums">{catCounts.get(c.key)}</span> : null}
              </button>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
            {TABS.map((t) => (
              <button key={t.key} type="button" onClick={() => setTab(t.key)}
                      className={`shrink-0 px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === t.key ? "border-bardin-orange text-bardin-orange" : "border-transparent text-slate-500"}`}>
                {t.label}<span className="ml-1.5 text-xs text-slate-400 tabular-nums">{counts[t.key]}</span>
              </button>
            ))}
          </div>

          {/* List */}
          {loading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => <div key={i} className="h-24 rounded-xl bg-slate-100 animate-pulse" />)}
            </div>
          ) : list.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
              {tab === "new" && (canFile ? "Nothing new. Need something from Ops? Tap + New request, or 🛠 Request from Ops on any job." : "Nothing new.")}
              {tab === "in_progress" && "Nothing in progress."}
              {tab === "waiting" && "Nothing waiting on anyone."}
              {tab === "done" && "Nothing closed in the last 60 days."}
            </div>
          ) : (
            <div className="space-y-3">
              {list.map((r) => (
                <OpsRequestCard key={r.id} r={r} actor={actor} roster={roster} onChanged={() => void load()}
                                hasOpenQuestion={openQ.has(r.deal_id)} />
              ))}
            </div>
          )}

          <p className="text-[11px] text-slate-400 pt-2">
            Order: urgent → needed-by → oldest. Amber at {3} days open, red at {7} or past needed-by.
            {seeAll ? " Reassign (leadership) hands a request to someone covering; new requests still go to the Roof Team Manager." : ""}
          </p>
        </>
      )}

      {member?.id && (
        <OpsRequestForm open={formOpen} onClose={() => setFormOpen(false)} actorId={member.id}
                        onFiled={() => { void load(); }} />
      )}
    </div>
  );
}

function Stat({ v, l, sub, tone }: { v: number | string; l: string; sub?: string; tone?: "rose" }) {
  return (
    <div className="rounded-lg border border-slate-200 px-3 py-2">
      <div className={`text-xl font-bold tabular-nums ${tone === "rose" ? "text-rose-600" : "text-bardin-ink"}`}>{v}</div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{l}</div>
      {sub && <div className={`text-[11px] ${tone === "rose" ? "text-rose-600" : "text-slate-400"}`}>{sub}</div>}
    </div>
  );
}
